import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const JOBS = {
  isrc: {
    start: "/isrc/fill-all", latest: "/isrc/jobs/latest", label: "Fill ISRCs", testid: "fill-isrcs-btn",
    line: (j) => `Filling ISRCs… ${j.progress_count || 0} / ${j.total_count || 0} · ${j.filled_count || 0} filled · ${j.review_count || 0} need review`,
    done: (j) => `ISRC fill done · ${j.filled_count || 0} filled · ${j.review_count || 0} need review · ${j.no_match_count || 0} no match`,
  },
  group: {
    start: "/projects/backfill", latest: "/projects/backfill/latest", label: "Group into Projects", testid: "group-projects-btn",
    line: (j) => `Grouping tracks… ${j.progress || 0} / ${j.group_count || 0} releases`,
    done: (j) => `Grouping done · ${j.projects_created || 0} projects created · ${j.tracks_attached || 0} tracks attached · ${j.artwork_upgraded || 0} covers upgraded`,
  },
};

export default function LibraryActions({ onChanged }) {
  const isrc = useJob("isrc", onChanged);
  const group = useJob("group", onChanged);
  return (
    <div className="library-actions" data-testid="library-actions">
      <div className="job-buttons">
        {[isrc, group].map((j) => (
          <button key={j.kind} className="btn btn-light job-btn" onClick={j.start} disabled={j.running} data-testid={j.cfg.testid}>
            {j.running ? "Working…" : j.cfg.label}
          </button>
        ))}
      </div>
      {[isrc, group].map((j) => j.text && (
        <span key={j.kind} className="data-line job-line" data-testid={`${j.kind}-job-status`}>{j.text}</span>
      ))}
    </div>
  );
}

function useJob(kind, onChanged) {
  const cfg = JOBS[kind];
  const [job, setJob] = useState(null);
  const timer = useRef(null);

  const poll = useCallback(async (initial = false) => {
    try {
      const r = await api.get(cfg.latest);
      const active = ["pending", "running"].includes(r.data?.status);
      if (!initial || active) setJob(r.data);
      if (active) timer.current = setTimeout(poll, 1500);
      else if (!initial && r.data?.status === "done") onChanged?.();
    } catch {
      timer.current = setTimeout(poll, 4000);
    }
  }, [cfg.latest, onChanged]);

  useEffect(() => {
    poll(true);
    return () => clearTimeout(timer.current);
  }, [poll]);

  const start = async () => {
    try {
      await api.post(cfg.start);
      setJob({ status: "pending" });
      poll();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 402 && det?.reason) {
        window.dispatchEvent(new CustomEvent("lq:paywall", { detail: { reason: det.reason } }));
      } else if (e.response?.status === 409) {
        toast(typeof det === "string" ? det : "Already running");
        poll();
      } else toast.error(typeof det === "string" ? det : "Could not start");
    }
  };

  const running = ["pending", "running"].includes(job?.status);
  const text = !job?.status ? "" : running ? cfg.line(job) : job.status === "done" ? cfg.done(job) : job.status === "failed" ? "Run failed — try again" : "";
  return { kind, cfg, start, running, text };
}
